import { useEffect } from "react";
import { reportClientError } from "@/lib/report-client-error";

const OPEN_LAYER_SELECTOR = [
  "[data-radix-popper-content-wrapper]",
  "[role='dialog'][data-state='open']",
  "[role='listbox'][data-state='open']",
  "[role='menu'][data-state='open']",
  "[data-radix-select-viewport]",
].join(",");

const CHECK_DELAY_MS = 350;

/**
 * Safari/WebKit safety net: Radix layers set `pointer-events: none` on <body>
 * while open and are expected to clear it on close. When the restore is missed
 * the whole page becomes unclickable. This watches <body> style changes and
 * clears a stale lock when no floating layer remains open.
 */
export default function PointerEventsGuard() {
  useEffect(() => {
    if (typeof document === "undefined") return;
    let timer: ReturnType<typeof setTimeout> | null = null;
    let reported = false;

    const check = () => {
      timer = null;
      if (document.body.style.pointerEvents !== "none") return;
      if (document.querySelector(OPEN_LAYER_SELECTOR)) {
        // Still open: re-check later in case the close is missed afterwards.
        timer = setTimeout(check, CHECK_DELAY_MS);
        return;
      }
      document.body.style.pointerEvents = "";
      if (!reported) {
        reported = true;
        void reportClientError(new Error("Stale body pointer-events:none cleared by PointerEventsGuard"), {
          boundary: "pointer-events-guard",
        });
      }
    };

    const observer = new MutationObserver(() => {
      if (document.body.style.pointerEvents === "none" && timer === null) {
        timer = setTimeout(check, CHECK_DELAY_MS);
      }
    });
    observer.observe(document.body, { attributes: true, attributeFilter: ["style"] });

    return () => {
      observer.disconnect();
      if (timer) clearTimeout(timer);
    };
  }, []);

  return null;
}
